export interface SlaPolicy {
  policyId: string
  version: number
  reporterEntityCode: string
  reporterEntityType: string
  productScope: string
  priority: string
  intakeChannel: string
  respondAfterSeconds: number
  resolveAfterSeconds: number
  enabled: boolean
  createdAt: string
  retiredAt?: string | null
}

export const WILDCARD = '*'

/**
 * Renders a duration the way an SLA target is actually discussed — "30 min",
 * "4 h", "14 d" — rather than as a raw second count.
 *
 * Seconds are what the API carries because they are unambiguous; nobody
 * negotiates a response time in seconds, and 1209600 is not a number a reader
 * converts correctly at a glance.
 */
export interface DurationParts {
  value: number
  /** Translation key for the unit, resolved by the caller. */
  unit: 'd' | 'h' | 'min' | 's'
}

/**
 * Splits a duration into a number and a unit KEY rather than returning a
 * formatted string.
 *
 * The unit has to be translated — a hardcoded "h" renders as a Latin letter
 * beside Persian digits in the Farsi UI, which is the one place this screen
 * would look half-localized. Returning parts keeps the arithmetic testable
 * without pulling the localization layer into a pure helper.
 */
export function durationParts(seconds: number): DurationParts | null {
  if (!Number.isFinite(seconds) || seconds <= 0) return null
  if (seconds % 86400 === 0) return { value: seconds / 86400, unit: 'd' }
  if (seconds % 3600 === 0) return { value: seconds / 3600, unit: 'h' }
  if (seconds % 60 === 0) return { value: seconds / 60, unit: 'min' }
  return { value: seconds, unit: 's' }
}

/**
 * True when the policy is a per-customer override rather than a platform
 * default. The wildcard is a literal '*', not an empty string — an empty
 * selector value would match nothing, since the server compares with
 * `IN (?, '*')`.
 */
export function isCustomerOverride(policy: SlaPolicy): boolean {
  return policy.reporterEntityCode !== WILDCARD
}

/**
 * The platform catch-all cannot be retired: ticket creation fails outright
 * when no policy matches, so removing it stops every customer opening a
 * ticket rather than relaxing a target. The server refuses it; this exists so
 * the UI does not offer an action that can only fail.
 */
export const PLATFORM_CATCH_ALL_POLICY_ID = 'default-24x7'

export function canRetire(policy: SlaPolicy): boolean {
  if (policy.policyId === PLATFORM_CATCH_ALL_POLICY_ID) return false
  return policy.enabled && !policy.retiredAt
}

/**
 * Describes which tickets a policy governs, in words rather than as five
 * columns of '*'.
 *
 * A row of wildcards is technically complete and practically unreadable: the
 * reader has to know that '*' means "any" in five different positions to see
 * that a policy applies to everything.
 */
/**
 * Returns an EMPTY array for a policy that narrows nothing, rather than the
 * words "all tickets" — that string is user-facing and has to be translated by
 * the caller, and a pure helper should not reach into localization.
 */
export function selectorParts(policy: SlaPolicy): string[] {
  const parts: string[] = []
  if (policy.reporterEntityCode !== WILDCARD) {
    parts.push(`${policy.reporterEntityType}:${policy.reporterEntityCode}`)
  }
  if (policy.priority !== WILDCARD) parts.push(policy.priority)
  if (policy.productScope !== WILDCARD) parts.push(policy.productScope)
  if (policy.intakeChannel !== WILDCARD) parts.push(policy.intakeChannel)
  return parts
}

export type DurationUnit = 'd' | 'h' | 'min'

const UNIT_SECONDS: Record<DurationUnit, number> = {
  d: 86400,
  h: 3600,
  min: 60
}

export const DURATION_UNITS: DurationUnit[] = ['min', 'h', 'd']

/**
 * Converts a value + unit into seconds.
 *
 * The form never asks for seconds. A response target is agreed as "30 minutes"
 * or "4 hours", and making an admin do that arithmetic is how a policy ends up
 * an order of magnitude out with nothing to catch it — the API accepts any
 * positive integer.
 */
export function toSeconds(value: number, unit: DurationUnit): number {
  return Math.round(value * UNIT_SECONDS[unit])
}

export interface SlaPolicyDraft {
  policyId: string
  reporterEntityCode: string
  reporterEntityType: string
  priority: string
  respondValue: string
  respondUnit: DurationUnit
  resolveValue: string
  resolveUnit: DurationUnit
}

export interface DraftErrors {
  policyId?: string
  reporterEntityCode?: string
  reporterEntityType?: string
  priority?: string
  respondValue?: string
  resolveValue?: string
}

/**
 * Validates a draft against the same rules the server enforces.
 *
 * This is fail-fast UX, NOT the boundary — ticket-service rejects every one of
 * these independently. It exists so an admin sees which field is wrong instead
 * of a constraint name, and the messages are keys the caller translates.
 */
export function validateDraft(draft: SlaPolicyDraft): DraftErrors {
  const errors: DraftErrors = {}
  if (!draft.policyId.trim()) errors.policyId = 'Policy id is required'
  if (!draft.reporterEntityCode.trim()) {
    errors.reporterEntityCode = 'Entity code is required'
  }
  // Codes are unique WITHIN an entity type, not across them, so a concrete
  // code without a concrete type would let a policy for accommodation X also
  // match channel X. The server has a CHECK constraint for exactly this.
  if (
    draft.reporterEntityCode.trim() !== WILDCARD &&
    draft.reporterEntityType.trim() === WILDCARD
  ) {
    errors.reporterEntityType =
      'Entity type is required for a customer override'
  }
  if (!draft.priority.trim()) errors.priority = 'Priority is required'

  const respond = Number(draft.respondValue)
  const resolve = Number(draft.resolveValue)
  if (!Number.isFinite(respond) || respond <= 0) {
    errors.respondValue = 'Respond target must be greater than zero'
  }
  if (!Number.isFinite(resolve) || resolve <= 0) {
    errors.resolveValue = 'Resolve target must be greater than zero'
  }
  if (!errors.respondValue && !errors.resolveValue) {
    // Compared in SECONDS, not in the entered numbers: "90 min" respond and
    // "1 h" resolve is 90 > 60 and invalid, while the raw values read 90 vs 1
    // and look fine. Comparing before conversion is the bug this avoids.
    if (
      toSeconds(resolve, draft.resolveUnit) <
      toSeconds(respond, draft.respondUnit)
    ) {
      errors.resolveValue = 'Resolve target must not be earlier than respond'
    }
  }
  return errors
}

export function hasErrors(errors: DraftErrors): boolean {
  return Object.keys(errors).length > 0
}

/**
 * Seeds a draft from an existing policy, or from a blank override.
 *
 * Editing keeps the policy id fixed — it is the identity a version chain hangs
 * off, and changing it creates a second policy rather than editing this one.
 */
export function draftFromPolicy(policy?: SlaPolicy): SlaPolicyDraft {
  if (!policy) {
    return {
      policyId: '',
      reporterEntityCode: '',
      reporterEntityType: 'accommodation',
      priority: 'P1',
      respondValue: '',
      respondUnit: 'h',
      resolveValue: '',
      resolveUnit: 'h'
    }
  }
  const respond = durationParts(policy.respondAfterSeconds)
  const resolve = durationParts(policy.resolveAfterSeconds)
  return {
    policyId: policy.policyId,
    reporterEntityCode: policy.reporterEntityCode,
    reporterEntityType: policy.reporterEntityType,
    priority: policy.priority,
    // Seconds that do not divide evenly fall back to minutes rather than
    // showing a fractional hour that would round on save.
    respondValue: String(
      respond?.unit === 's'
        ? policy.respondAfterSeconds / 60
        : respond?.value ?? ''
    ),
    respondUnit:
      respond?.unit === 's' ? 'min' : (respond?.unit as DurationUnit) ?? 'h',
    resolveValue: String(
      resolve?.unit === 's'
        ? policy.resolveAfterSeconds / 60
        : resolve?.value ?? ''
    ),
    resolveUnit:
      resolve?.unit === 's' ? 'min' : (resolve?.unit as DurationUnit) ?? 'h'
  }
}

/**
 * Sorts the way the server ranks: customer overrides first, then by policy id,
 * newest version first.
 *
 * This is presentation only and must not be mistaken for the precedence rule.
 * Which policy actually governs a ticket is answered by resolveTicketSlaPolicy
 * on the server, using the create path's own ordering — re-deriving it here
 * would put a second copy of the one thing that fails silently in the browser.
 */
export function sortPolicies(policies: SlaPolicy[]): SlaPolicy[] {
  return [...policies].sort((a, b) => {
    const aOverride = isCustomerOverride(a) ? 0 : 1
    const bOverride = isCustomerOverride(b) ? 0 : 1
    if (aOverride !== bOverride) return aOverride - bOverride
    if (a.policyId !== b.policyId) return a.policyId < b.policyId ? -1 : 1
    return b.version - a.version
  })
}
